import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends Component {
  /**
   * IntersectionObserver: section が画面に近づいたら読み込み開始
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * ⚠️ 当店の改修（2026-08-10）：監視の開始を window の load イベントまで遅らせる。
   *
   * 背景：トップページで最上部から手でスクロールして本セクションを表示させると、
   * Section Rendering API の応答がブラウザ側で15秒以上かかることがある不具合の調査。
   * Shopify側のサーバー処理自体は1秒未満（Server-Timingで確認済み）で、
   * クライアント側で応答を待っている間の遅延だった。
   *
   * ⚠️ この変更を本番へ入れた後の実機検証で、効果は確認できなかった
   * （同じ手順で依然として遅延が発生した）。「画像の遅延読み込みと接続を
   * 奪い合っている」という仮説に基づく対策だったが、原因はまだ特定できていない。
   * 副作用が無い変更（表示専用・購入導線に無関係）なので撤去はしていないが、
   * これが根本対策だとは考えないこと。次に調査するときはここから疑わない。
   *
   * 調査の詳細と、次に試すべき切り分け方法は WORK_LOG.md を参照。
   */
  #startObserving = () => {
    this.#intersectionObserver.observe(this);
  };

  connectedCallback() {
    super.connectedCallback();

    if (document.readyState === 'complete') {
      this.#startObserving();
    } else {
      window.addEventListener('load', this.#startObserving, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('load', this.#startObserving);
    this.#intersectionObserver.disconnect();
  }

  /**
   * localStorageからIDを取得し、Section Rendering APIで商品を描画する
   */
  async #loadRecentlyViewed() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const viewedIds = RecentlyViewed.getProducts();
    if (viewedIds.length === 0) {
      this.classList.add('hidden');
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', viewedIds.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (html?.trim()) {
        this.dataset.performed = 'true';
        morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      } else {
        this.classList.add('hidden');
      }
    } catch (e) {
      console.error('Recently viewed products error:', e.message);
      this.classList.add('hidden');
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

